// A copy of shared/src/utils/uploadFileTypes.json; a shared test fails when the two differ.
import UPLOAD_FILE_TYPES from "./uploadFileTypes.json";

export type UploadExtension = keyof typeof UPLOAD_FILE_TYPES;

export const UPLOAD_EXTENSIONS = Object.keys(
  UPLOAD_FILE_TYPES,
) as UploadExtension[];

const isUploadExtension = (extension: string): extension is UploadExtension =>
  Object.prototype.hasOwnProperty.call(UPLOAD_FILE_TYPES, extension);

const extensionOf = (filename: string): string => {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
};

/** `accept` attribute for a file input that takes these extensions. */
export const fileAccept = (extensions: readonly UploadExtension[]): string =>
  extensions.map((extension) => `.${extension}`).join(", ");

export const UPLOAD_FILE_ACCEPT = fileAccept(UPLOAD_EXTENSIONS);

/** The content type a stored file with this extension is shown as. */
export const mimeForExtension = (extension?: string | null) => {
  const normalized = (extension ?? "").toLowerCase();
  return isUploadExtension(normalized)
    ? UPLOAD_FILE_TYPES[normalized][0]
    : undefined;
};

/** The server's rule: a listed extension, and a type those extensions are sent with or none. */
const isAcceptedFile = (
  file: File,
  extensions: readonly UploadExtension[],
): boolean => {
  const extension = extensionOf(file.name);
  if (!isUploadExtension(extension) || !extensions.includes(extension)) {
    return false;
  }
  return (
    file.type === "" ||
    file.type === "application/octet-stream" ||
    extensions.some((allowed) => UPLOAD_FILE_TYPES[allowed].includes(file.type))
  );
};

/** Splits a selection for the caller to toast the rest; `accept` never filters a dragged file. */
export const partitionUploadFiles = (
  files: FileList | File[],
  extensions: readonly UploadExtension[],
): { supported: File[]; rejected: string[] } => {
  const supported: File[] = [];
  const rejected: string[] = [];
  Array.from(files).forEach((file) => {
    if (isAcceptedFile(file, extensions)) supported.push(file);
    else rejected.push(file.name);
  });
  return { supported, rejected };
};
